import { useState, useRef, useEffect } from "react";
import PropTypes from 'prop-types';
import axios from "axios";
import { API } from "@/config";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, X, Loader2 } from "lucide-react";
import { formatErrorMessage } from "../lib/utils";

export default function ImageUpload({
    token,
    uploadEndpoint, // e.g., "/profile/upload-url" or "/clinics/{id}/upload-url"
    updateEndpoint, // e.g., "/profile/images" or "/clinics/{id}/images"
    downloadEndpoint, // e.g., "/profile/download-url"
    imageType, // e.g., "signature", "stamp", "logo"
    currentUrl,
    onUploadComplete,
    label = "Upload Image",
    accept = "image/png,image/jpeg"
}) {
    const [isUploading, setIsUploading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(currentUrl);
    const fileInputRef = useRef(null);

    // Sync preview with currentUrl when it changes (e.g. initial load)
    useEffect(() => {
        const fetchDownloadUrl = async () => {
            if (!currentUrl) {
                setPreviewUrl(null);
                return;
            }

            // If it's already a full URL (blob or http), use it
            if (currentUrl.startsWith("blob:") || currentUrl.startsWith("http")) {
                setPreviewUrl(currentUrl);
                return;
            }

            // If we have a key and a download endpoint, fetch the presigned URL
            if (downloadEndpoint && token) {
                try {
                    const response = await axios.post(
                        `${API}${downloadEndpoint}`,
                        { key: currentUrl },
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                    setPreviewUrl(response.data.download_url);
                } catch (error) {
                    console.error("Failed to fetch download URL:", error);
                    // Fallback to showing nothing if fetch fails
                }
            }
        };

        fetchDownloadUrl();
    }, [currentUrl, downloadEndpoint, token]);

    const handleFileSelect = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        const validTypes = accept.split(",");
        if (!validTypes.some(type => file.type === type.trim())) {
            toast.error("Invalid file type. Please upload an image.");
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            toast.error("File too large. Maximum size is 5MB.");
            return;
        }

        setIsUploading(true);

        try {
            // Get content type from file
            const contentType = file.type || "image/png";

            // Step 1: Get presigned upload URL
            const presignedResponse = await axios.post(
                `${API}${uploadEndpoint}`,
                {
                    file_type: imageType,
                    content_type: contentType
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            const { upload_url, object_key: key } = presignedResponse.data;

            // Step 2: Upload file to S3 using presigned URL
            await axios.put(upload_url, file, {
                headers: {
                    "Content-Type": contentType
                },
                withCredentials: false
            });

            // Step 3: Update the entity with the new image URL
            const updateData = {};
            updateData[`${imageType}_url`] = key;

            await axios.put(
                `${API}${updateEndpoint}`,
                updateData,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            // Show preview
            setPreviewUrl(URL.createObjectURL(file));

            toast.success(`${label} uploaded successfully!`);
            onUploadComplete?.(key);

        } catch (error) {
            console.error("Upload error:", error);
            if (error.response?.status === 503) {
                toast.error("Storage not configured. Please set up S3/MinIO.");
            } else {
                toast.error(formatErrorMessage(error));
            }
        } finally {
            setIsUploading(false);
            // Reset input
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
    };

    const handleRemove = async () => {
        if (!previewUrl) return;

        try {
            const updateData = {};
            updateData[`${imageType}_url`] = null;

            await axios.put(
                `${API}${updateEndpoint}`,
                updateData,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setPreviewUrl(null);
            toast.success("Image removed");
            onUploadComplete?.(null);
        } catch (error) {
            console.error("Remove error:", error);
            toast.error("Failed to remove image");
        }
    };

    return (
        <div style={{
            border: "1px dashed #e2e8f0",
            borderRadius: "12px",
            padding: "1.5rem",
            background: "#fafafa"
        }}>
            <div style={{ marginBottom: "0.75rem", fontWeight: "500", fontSize: "0.875rem" }}>
                {label}
            </div>

            {previewUrl ? (
                <div style={{ position: "relative", width: "100%", overflow: "hidden" }}>
                    <img
                        src={previewUrl}
                        alt={label}
                        style={{
                            width: "100%",
                            maxHeight: "100px",
                            objectFit: "contain",
                            borderRadius: "8px",
                            border: "1px solid #e2e8f0"
                        }}
                    />
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleRemove}
                        style={{
                            position: "absolute",
                            top: "4px",
                            right: "4px",
                            width: "24px",
                            height: "24px",
                            borderRadius: "50%",
                            background: "#ef4444",
                            color: "white"
                        }}
                    >
                        <X size={14} />
                    </Button>
                </div>
            ) : (
                <div
                    onClick={() => !isUploading && fileInputRef.current?.click()}
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "2rem",
                        border: "2px dashed #cbd5e1",
                        borderRadius: "8px",
                        background: "white",
                        cursor: isUploading ? "not-allowed" : "pointer",
                        transition: "border-color 0.2s"
                    }}
                    onMouseEnter={(e) => !isUploading && (e.currentTarget.style.borderColor = "#0ea5e9")}
                    onMouseLeave={(e) => e.currentTarget.style.borderColor = "#cbd5e1"}
                >
                    {isUploading ? (
                        <>
                            <Loader2 size={32} style={{ color: "#0ea5e9", marginBottom: "0.5rem" }} className="animate-spin" />
                            <span style={{ color: "#64748b", fontSize: "0.875rem" }}>Uploading...</span>
                        </>
                    ) : (
                        <>
                            <Upload size={32} style={{ color: "#94a3b8", marginBottom: "0.5rem" }} />
                            <span style={{ color: "#64748b", fontSize: "0.875rem" }}>Click to upload</span>
                            <span style={{ color: "#94a3b8", fontSize: "0.75rem", marginTop: "0.25rem" }}>PNG, JPG up to 5MB</span>
                        </>
                    )}
                </div>
            )}

            <input
                ref={fileInputRef}
                type="file"
                accept={accept}
                onChange={handleFileSelect}
                style={{ display: "none" }}
            />
        </div>
    );
}

ImageUpload.propTypes = {
    token: PropTypes.string,
    uploadEndpoint: PropTypes.string,
    updateEndpoint: PropTypes.string,
    downloadEndpoint: PropTypes.string,
    imageType: PropTypes.string,
    currentUrl: PropTypes.string,
    onUploadComplete: PropTypes.func,
    label: PropTypes.string,
    accept: PropTypes.string
};
